import React from "react";
import { Droppable, DroppableProvided, DroppableStateSnapshot } from "react-beautiful-dnd";
import ReactDraggable, { DraggableData, DraggableEvent } from "react-draggable";
import { FlowConstants } from "../../rule-engine-core/Flow/Utilities/FlowConstants";
import { FlowEditContext } from "../FlowEditContext";
import styled from "styled-components";
import { IPosition } from "./Common/IPosition";
import { IFlowStepSequence } from "../../rule-engine-core/Flow/Structure/IFlowStepSequence";
import { CandidateSequence } from "../../rule-engine-core/Flow/Structure/CandidateSequence";
import { FlowStepOutputInstructionType } from "../../rule-engine-core/Flow/Structure/FlowStepOutputInstructions";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { FlowSequenceCompleteStrategy } from "../../rule-engine-core/Flow/Structure/FlowSequenceCompleteStrategy";

import { FlowUtilities } from "../../rule-engine-core/Flow/Utilities/FlowUtilities";
import { FlowStepData } from "../../rule-engine-core/Flow/Steps/FlowStepData";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export const CandidateSequenceDiv = styled("div")<{
    show: boolean;
    isDragging: boolean;
    position: IPosition;
    draggingPosition: {
        x: number;
        y: number;
    };
}>`
    position: absolute;
    top: ${(props) => (props.isDragging ? props.draggingPosition.y : props.position.y)}px;
    left: ${(props) => (props.isDragging ? props.draggingPosition.x : props.position.x)}px;
    opacity: ${(props) => (props.show ? 1 : 0)};
    background: transparent;
    border-radius: 4px;
    ${(props) => props.isDragging && `z-index: 2;`}
    ${(props) => !props.isDragging && `transform: none !important;`}
`;

export const CandidateSequenceDropDiv = styled("div")<{
    isDroppingOver: boolean;
    isPathCandidate: boolean;
    isDragging: boolean;
    size: {
        width: number;
        height: number;
        dragging: {
            width: number;
            height: number;
        };
    };
}>`
    border-radius: 4px;
    padding: 4px;
    display: flex;
    flex-direction: column;

    ${(props) => props.isDroppingOver && `border: solid 1px ${FlowConstants.SSS_STEP_DROP_OVER_BORDER_COLOR};`}

    ${(props) =>
        !props.isPathCandidate &&
        `
		${
            props.isDroppingOver
                ? `background: ${FlowConstants.DROPPING_COLOR};`
                : `border: dashed 1px ${UIStyleConstants.GRAY_COLOR};`
        };
	`}

  ${(props) =>
        props.isDragging
            ? `
			width: ${props.size.dragging.width}px;
			min-height: ${props.size.dragging.height}px;
		`
            : `
			width: ${props.size.width}px;
			min-height: ${props.size.height}px;
		`}

  ${(props) =>
        props.isPathCandidate &&
        `
		background: ${UIStyleConstants.WHITE_DARK_COLOR};
	`}
`;

export const CandidateSequenceDropTitleDiv = styled.div<{
    size: {
        padding: string;
        fontSize: number;
    };
    isDragging: boolean;
}>`
    border-radius: 4px;
    display: flex;
    width: 100%;
    height: 100%;
    border: 1px dashed ${UIStyleConstants.GRAY_DARK_COLOR};
    flex: 1;

    font-style: normal;
    font-weight: 500;
    font-size: ${(props) => props.size.fontSize}px;
    line-height: 120%;

    align-items: center;
    text-align: center;

    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    cursor: pointer;

    padding: ${(props) => (props.isDragging ? "10px" : props.size.padding)};
`;

interface CandidateSequenceStackProps {
    editContext: FlowEditContext;
    candidate: IFlowStepSequence;
}

export class CandidateSequenceStack extends React.Component<
    CandidateSequenceStackProps,
    { isDragging: boolean; x: number; y: number }
> {
    constructor(props: CandidateSequenceStackProps) {
        super(props);

        this.state = {
            isDragging: false,
            x: 0,
            y: 0,
        };
    }

    getDefaultPosition = (): IPosition => {
        return { x: this.props.candidate.x, y: this.props.candidate.y };
    };

    onDragStart = (e: DraggableEvent) => {
        this.props.editContext.clearSelection();
        let candidateSize = this.props.editContext.getCandidateCommonStyles();

        let mouseX = (e as React.MouseEvent<HTMLDivElement>).clientX;
        let mouseY = (e as React.MouseEvent<HTMLDivElement>).clientY;

        let rect = (e as React.MouseEvent<HTMLDivElement>).currentTarget.getBoundingClientRect();

        let x = mouseX - candidateSize.dragging.width / 2 - rect.left;
        let y = mouseY - candidateSize.dragging.height / 2 - rect.top;
        this.setState({
            isDragging: true,
            x,
            y,
        });
    };

    onDrag = (_e: DraggableEvent, data: DraggableData) => {
        let sequence = this.props.editContext.getSequenceByXY(data.x, data.y);
        if (sequence) {
            this.props.editContext.dragOverSequence = sequence;
        } else {
            this.props.editContext.dragOverSequence = null;
        }

        StateManager.propertyChanged(this.props.editContext, FlowEditContext.DRAG_OVER_SEQUENCE);
    };

    onDragStop = (_e: DraggableEvent, data: DraggableData) => {
        let sequence = this.props.editContext.getSequenceByXY(data.x, data.y);
        this.props.editContext.dragOverSequence = null;

        if (sequence) {
            if (sequence.isNormal) {
                this.props.editContext.combineSequences(this.props.candidate, sequence);
            } else if (this.props.candidate instanceof CandidateSequence) {
                let step = this.props.editContext.flow.find(this.props.candidate.instruction.stepId) as FlowStepData;
                if (!step) return;

                let seq = this.props.editContext.flow.findSequenceByStepId(step._id);
                if (!seq) return;

                if (sequence.isEnd) {
                    // for sequence strategy
                    if (FlowUtilities.IsEndStep(step)) {
                        seq.strategy = FlowSequenceCompleteStrategy.END_FLOW;
                    } else {
                        // for step strategy
                        this.props.candidate.instruction.strategy = FlowStepOutputInstructionType.END_FLOW;
                        StateManager.changed(seq);
                    }
                } else if (sequence.isError) {
                    if (FlowUtilities.IsEndStep(step)) {
                        // for sequence strategy
                        seq.strategy = FlowSequenceCompleteStrategy.END_FLOW_IN_ERROR;
                    } else {
                        // for step strategy
                        this.props.candidate.instruction.strategy = FlowStepOutputInstructionType.THROW_EXCEPTION;
                        StateManager.changed(seq);
                    }
                }
            }
        }

        this.setState({
            isDragging: false,
            x: 0,
            y: 0,
        });

        this.props.editContext.refreshLines();
    };

    render() {
        let droppableId = this.props.candidate._id;

        let isStepCandidate = Reflect.get(this.props.candidate, "instruction") !== undefined;

        let candidateSize = this.props.editContext.getCandidateCommonStyles();

        return (
            <ReactDraggable
                disabled={!isStepCandidate}
                onStop={this.onDragStop}
                onDrag={this.onDrag}
                onStart={this.onDragStart}
                position={this.getDefaultPosition()}
                defaultPosition={this.getDefaultPosition()}
            >
                <CandidateSequenceDiv
                    id={this.props.candidate.getElementId()}
                    position={this.getDefaultPosition()}
                    isDragging={this.state.isDragging}
                    draggingPosition={this.state}
                    show={isStepCandidate || !!this.props.editContext.draggingControl}
                >
                    <Droppable droppableId={droppableId}>
                        {(dropProvided: DroppableProvided, dropSnapshot: DroppableStateSnapshot) => {
                            return (
                                <CandidateSequenceDropDiv
                                    isPathCandidate={isStepCandidate}
                                    isDroppingOver={dropSnapshot.isDraggingOver}
                                    isDragging={this.state.isDragging}
                                    size={candidateSize}
                                    {...dropProvided.droppableProps}
                                    ref={dropProvided.innerRef}
                                >
                                    <CandidateSequenceDropTitleDiv
                                        size={candidateSize}
                                        isDragging={this.state.isDragging}
                                    >
                                        Drop step here or drag to link
                                    </CandidateSequenceDropTitleDiv>
                                    <div style={{ display: "none" }}>{dropProvided.placeholder}</div>
                                </CandidateSequenceDropDiv>
                            );
                        }}
                    </Droppable>
                </CandidateSequenceDiv>
            </ReactDraggable>
        );
    }
}
